import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SessionExpiryWarning } from "@/components/SessionExpiryWarning";
import { DualScrollX } from "@/components/DualScrollX";
import { AdminNoteInline } from "@/components/AdminNoteInline";
import axios from "axios";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import CourseSchedulePanel from "@/pages/admin/CourseSchedulePanel";
import GoogleReviewsPanel from "@/pages/admin/GoogleReviewsPanel";
import { Download, LogOut, Inbox, Star, Trash2, Plus, Eye, EyeOff, RefreshCw, ShieldCheck, CalendarClock, Save, X as XIcon, KeyRound, BarChart3, Phone, MessageCircle, TrendingUp, ExternalLink, Send, Newspaper, Video, Image as ImageIcon, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ArticleBody } from "@/components/ArticleBody";
import { API, FARO_LOGO } from "@/lib/faro";
import { trackClick } from "@/lib/analytics";

const CENTER_NAMES = {
  coruxo: "Coruxo", panxon: "Panxón", nigran: "Nigrán", acaniza: "A Cañiza", plazaamerica: "VIGO - Plaza América",
};

const STATUS_OPTIONS = [
  { value: "nuevo", label: "Nuevo", cls: "bg-[var(--faro-blue)] text-white" },
  { value: "contactado", label: "Contactado", cls: "bg-green-600 text-white" },
  { value: "perdido", label: "Perdido", cls: "bg-gray-500 text-white" },
  { value: "eliminado", label: "Eliminado", cls: "bg-[var(--faro-red)] text-white" },
];

const POINTS_STATUS_OPTIONS = [
  STATUS_OPTIONS[0],
  { value: "pendiente", label: "Pendiente", cls: "bg-orange-500 text-white" },
  STATUS_OPTIONS[1],
  { value: "matriculado", label: "Matriculado", cls: "bg-emerald-800 text-white" },
  STATUS_OPTIONS[2],
  STATUS_OPTIONS[3],
];

function StatusPicker({ value, onChange, testid, options = STATUS_OPTIONS }) {
  const current = options.find((o) => o.value === value) || options[0];
  return (
    <div className="relative inline-block">
      <select
        value={current.value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={`text-[10px] uppercase tracking-wider font-black px-2.5 py-1.5 rounded-full appearance-none pr-6 cursor-pointer ${current.cls}`}
        style={{ backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 4px center", backgroundSize: "10px" }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ------- WhatsApp reminder helpers -------
const CONTACT_TEMPLATES = [
  {
    id: "recordatorio",
    label: "Recordatorio de solicitud",
    build: (l) => `Hola ${l.name || ""}, te escribo desde Autoescuelas Faro 👋\n\nRecibimos tu solicitud de información sobre ${l.permit_interest ? "el permiso " + l.permit_interest : "nuestros cursos"} en el centro de ${CENTER_NAMES[l.center] || l.center || "nuestro centro"}.\n\n¿Quieres que te llamemos o prefieres pasar por el centro? Estamos aquí para lo que necesites.`,
  },
  {
    id: "curso_disponible",
    label: "Curso próximo disponible",
    build: (l) => `Hola ${l.name || ""}, soy del equipo de Autoescuelas Faro.\n\nTenemos plazas para el próximo curso en ${CENTER_NAMES[l.center] || l.center || "nuestro centro"}${l.permit_interest ? " del permiso " + l.permit_interest : ""}. ¿Te reservamos plaza?\n\nCualquier duda, aquí estoy.`,
  },
  {
    id: "documentacion",
    label: "Solicitud de documentación",
    build: (l) => `Hola ${l.name || ""}, para arrancar tu matrícula en Autoescuelas Faro necesitamos: DNI en vigor, 1 foto tipo carnet y el informe psicotécnico.\n\n¿Cuándo te viene bien pasar por ${CENTER_NAMES[l.center] || "nuestro centro"}?`,
  },
];

const POINTS_TEMPLATES = [
  {
    id: "recordatorio_puntos",
    label: "Recordatorio inscripción puntos",
    build: (r) => `Hola ${r.full_name || ""}, te escribo desde Autoescuelas Faro 👋\n\nRecibimos tu inscripción para el curso de ${r.course_type || "permiso por puntos"} del mes de ${r.course_month || ""} en Plaza América.\n\nEstamos preparándolo todo. ¿Alguna duda o necesitas algo más?`,
  },
  {
    id: "confirmacion_puntos",
    label: "Confirmación de plaza",
    build: (r) => `Hola ${r.full_name || ""}, te confirmamos tu plaza en el curso de ${r.course_type || "permiso por puntos"} de ${r.course_month || ""} en Autoescuelas Faro (Plaza América).\n\nTe pasaremos el horario exacto y la documentación necesaria en breve. ¡Nos vemos pronto!`,
  },
  {
    id: "pago_puntos",
    label: "Recordatorio de pago",
    build: (r) => `Hola ${r.full_name || ""}, para reservar tu plaza en el curso de ${r.course_type || "puntos"} de ${r.course_month || ""} necesitamos formalizar el pago. ¿Puedes pasarte por Plaza América o prefieres transferencia?\n\nGracias!`,
  },
];

function normalizePhoneForWa(phone) {
  if (!phone) return "";
  let digits = String(phone).replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.length === 9) digits = "34" + digits; // Spanish mobile
  return digits;
}

function yesNoLabel(v) {
  if (v === "si") return "Sí";
  if (v === "no") return "No";
  return "—";
}

const MONTH_NAMES_ES = ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO", "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"];

// Estados del intento de contacto para leads/inscripciones sin WhatsApp.
const CONTACT_ATTEMPT_OPTIONS = [
  { value: "", label: "Sin WhatsApp · Llamar/SMS", cls: "bg-[var(--faro-red)] text-white" },
  { value: "called", label: "Ya llamé", cls: "bg-emerald-600 text-white" },
  { value: "no_answer", label: "Sin respuesta", cls: "bg-amber-500 text-white" },
];

function ContactAttemptChip({ value, onChange, testid }) {
  const current = CONTACT_ATTEMPT_OPTIONS.find((o) => o.value === (value || "")) || CONTACT_ATTEMPT_OPTIONS[0];
  return (
    <select
      value={current.value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      aria-label="Registrar intento de contacto"
      title="Registrar intento de contacto"
      className={`text-[9px] font-black uppercase tracking-wider rounded-full appearance-none pl-2.5 pr-5 py-0.5 cursor-pointer border-0 w-fit ${current.cls}`}
      style={{ backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 3px center", backgroundSize: "9px" }}
    >
      {CONTACT_ATTEMPT_OPTIONS.map((o) => (
        <option key={o.value || "empty"} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function courseMonthLabel(courseType, courseMonth) {
  // Renders "B1 SEPTIEMBRE 2026" or empty when either field is missing / non-scheduled type.
  if (!courseType || !courseMonth) return "";
  const parts = String(courseMonth).split("-");
  if (parts.length < 2) return "";
  const idx = parseInt(parts[1], 10) - 1;
  if (isNaN(idx) || idx < 0 || idx > 11) return "";
  return `${courseType} ${MONTH_NAMES_ES[idx]} ${parts[0]}`;
}

// Lista de opciones "YYYY-MM" desde el mes actual hasta N meses vista + un fallback
// para el `current` si cae fuera del rango (p.ej. una convocatoria pasada aún visible).
function buildMonthOptions(current, monthsAhead = 12) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < monthsAhead; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const value = `${y}-${m}`;
    out.push({ value, label: `${MONTH_NAMES_ES[d.getMonth()]} ${y}` });
  }
  // Si el mes actualmente asignado no aparece en la lista (por ejemplo un mes pasado),
  // lo añadimos al principio para no perderlo al abrir el select.
  if (current && !out.some((o) => o.value === current)) {
    const parts = String(current).split("-");
    if (parts.length === 2) {
      const idx = parseInt(parts[1], 10) - 1;
      if (!isNaN(idx) && idx >= 0 && idx < 12) {
        out.unshift({ value: current, label: `${MONTH_NAMES_ES[idx]} ${parts[0]}` });
      }
    }
  }
  return out;
}

function sortPointsRows(rows) {
  // Non-matriculated first (newest created_at at the top, as backend already returns).
  // Matriculated rows go to the bottom, ordered ascending by matriculated_at
  // (first enrolled first) — matches how the user asked for it: "por momento en que los anoté".
  const nonMat = [];
  const mat = [];
  for (const r of rows) (r.status === "matriculado" ? mat : nonMat).push(r);
  mat.sort((a, b) => {
    const ta = a.matriculated_at ? new Date(a.matriculated_at).getTime() : 0;
    const tb = b.matriculated_at ? new Date(b.matriculated_at).getTime() : 0;
    return ta - tb;
  });
  return [...nonMat, ...mat];
}

function WhatsappReminderModal({ open, onClose, subject, templates }) {
  const [tplId, setTplId] = useState(templates[0]?.id || "");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open) return;
    const t = templates.find((x) => x.id === tplId) || templates[0];
    setMessage(t ? t.build(subject) : "");
  }, [open, tplId, subject, templates]);

  if (!open || !subject) return null;

  const phone = normalizePhoneForWa(subject.phone);
  const canSend = phone && message.trim().length > 0;
  const waHref = canSend ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}` : "#";

  const sendAndOpen = () => {
    if (!canSend) return;
    trackClick("whatsapp", { center: subject.center || null, extra: { source: "admin_reminder", template: tplId } });
    window.open(waHref, "_blank", "noopener");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-testid="wa-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-[#25d366] text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-black opacity-90">Recordatorio WhatsApp</div>
            <div className="font-display font-black text-lg leading-tight">
              {subject.name || subject.full_name || "Alumno"}{" "}
              <span className="font-normal opacity-90 text-sm">· {subject.phone || "sin teléfono"}</span>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" aria-label="Cerrar" data-testid="wa-modal-close">
            <XIcon size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Plantilla</label>
            <select
              value={tplId}
              onChange={(e) => setTplId(e.target.value)}
              data-testid="wa-modal-template"
              className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)]"
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Mensaje (editable)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="wa-modal-message"
              rows={7}
              className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-3 outline-none focus:border-[var(--faro-blue)] resize-y font-sans"
            />
            <div className="text-[11px] text-[var(--faro-text-soft)] mt-1">{message.length} caracteres</div>
          </div>
          {!phone && (
            <div className="text-sm text-[var(--faro-red)] font-semibold bg-red-50 rounded-xl px-3 py-2">
              Este contacto no tiene teléfono válido — no podemos generar el enlace de WhatsApp.
            </div>
          )}
          <div className="flex items-center gap-2 justify-end">
            <button onClick={onClose} className="btn-pill btn-ghost text-sm" data-testid="wa-modal-cancel">Cancelar</button>
            <button
              onClick={sendAndOpen}
              disabled={!canSend}
              data-testid="wa-modal-send"
              className="btn-pill text-sm justify-center disabled:opacity-50"
              style={{ background: "#25d366", color: "#fff" }}
            >
              <Send size={14} /> Abrir en WhatsApp
            </button>
          </div>
          <p className="text-[11px] text-[var(--faro-text-soft)]">
            Se abrirá WhatsApp Web (o la app en tu móvil) con el mensaje listo. Sólo tienes que pulsar &ldquo;Enviar&rdquo; allí.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { isAuthed, checking, logout, authHeader, user, token } = useAuth();
  const [tab, setTab] = useState("leads");
  const [newCounts, setNewCounts] = useState({ contacts: 0, points: 0 });
  const [buildId, setBuildId] = useState("");

  useEffect(() => {
    fetch("/build.txt")
      .then((r) => (r.ok ? r.text() : ""))
      .then((t) => {
        const v = (t || "").trim();
        setBuildId(v && !v.startsWith("<") && v.length < 40 ? v : "dev");
      })
      .catch(() => setBuildId("dev"));
  }, []);

  const refreshCounts = useCallback(() => {
    axios.get(`${API}/admin/new-counts`, { headers: authHeader }).then(({ data }) => setNewCounts(data)).catch(() => {});
  }, [authHeader]);

  useEffect(() => {
    if (isAuthed) refreshCounts();
  }, [isAuthed, tab, refreshCounts]);

  if (!checking && !isAuthed) return <Navigate to="/admin/login" replace />;
  if (checking) return <div className="min-h-screen flex items-center justify-center text-[var(--faro-text-soft)]">Cargando...</div>;

  return (
    <div className="min-h-screen bg-[var(--faro-bg)]" data-testid="admin-dashboard">
      <header className="bg-[var(--faro-blue)] text-white">
        <div className="max-w-[1700px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg overflow-hidden border border-white/20">
              <img src={FARO_LOGO} alt="Faro" className="w-full h-full object-cover" />
            </div>
            <span className="font-display font-black">Panel Faro</span>
            {buildId && (
              <span
                data-testid="admin-build-badge"
                title="Versión desplegada (fecha-hora UTC del build). Si no coincide con tu último despliegue, la caché sirve una versión antigua."
                className="ml-2 px-2 py-0.5 rounded-full bg-white/10 text-white/70 text-[10px] font-bold tracking-wider"
              >
                build {buildId}
              </span>
            )}
          </Link>
          <button
            onClick={() => {
              if (!window.confirm("¿Borrar DEFINITIVAMENTE los registros marcados como eliminados hace más de 15 días?")) return;
              axios.post(`${API}/admin/cleanup-deleted`, {}, { headers: authHeader })
                .then(({ data }) => toast.success(`Purga completada: ${data.contacts_deleted} solicitudes y ${data.points_deleted} inscripciones borradas`))
                .catch(() => toast.error("No se pudo ejecutar la purga"));
            }}
            data-testid="admin-cleanup-btn"
            title="Borra definitivamente los registros en estado Eliminado con más de 15 días"
            className="btn-pill btn-ghost text-xs"
          >
            Purgar eliminados
          </button>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/70 hidden sm:inline">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="btn-pill bg-white/10 hover:bg-white/20 text-white text-sm">
              <LogOut size={15} /> Salir
            </button>
          </div>
        </div>
      </header>

      <SessionExpiryWarning token={token} onExpired={logout} />

      <div className="max-w-[1700px] mx-auto px-5 sm:px-8 py-8">
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab("leads")} data-testid="tab-leads" className={`btn-pill text-sm ${tab === "leads" ? "btn-secondary" : "btn-ghost"}`}>
            <Inbox size={16} /> Solicitudes
            {newCounts.contacts > 0 && (
              <span data-testid="tab-leads-badge" className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--faro-red)] text-white text-[11px] font-black leading-none">{newCounts.contacts}</span>
            )}
          </button>
          <button onClick={() => setTab("reviews")} data-testid="tab-reviews" className={`btn-pill text-sm ${tab === "reviews" ? "btn-secondary" : "btn-ghost"}`}>
            <Star size={16} /> Reseñas
          </button>
          <button onClick={() => setTab("google")} data-testid="tab-google" className={`btn-pill text-sm ${tab === "google" ? "btn-secondary" : "btn-ghost"}`}>
            <Star size={16} /> Reseñas Google
          </button>
          <button onClick={() => setTab("puntos")} data-testid="tab-puntos" className={`btn-pill text-sm ${tab === "puntos" ? "btn-secondary" : "btn-ghost"}`}>
            <ShieldCheck size={16} /> Permiso por puntos
            {newCounts.points > 0 && (
              <span data-testid="tab-puntos-badge" className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--faro-red)] text-white text-[11px] font-black leading-none">{newCounts.points}</span>
            )}
          </button>
          <button onClick={() => setTab("banner")} data-testid="tab-banner" className={`btn-pill text-sm ${tab === "banner" ? "btn-secondary" : "btn-ghost"}`}>
            <CalendarClock size={16} /> Próximo curso
          </button>
          <button onClick={() => setTab("schedule")} data-testid="tab-schedule" className={`btn-pill text-sm ${tab === "schedule" ? "btn-secondary" : "btn-ghost"}`}>
            <CalendarClock size={16} /> Cursos de puntos
          </button>
          <button onClick={() => setTab("account")} data-testid="tab-account" className={`btn-pill text-sm ${tab === "account" ? "btn-secondary" : "btn-ghost"}`}>
            <KeyRound size={16} /> Mi cuenta
          </button>
          <button onClick={() => setTab("stats")} data-testid="tab-stats" className={`btn-pill text-sm ${tab === "stats" ? "btn-secondary" : "btn-ghost"}`}>
            <BarChart3 size={16} /> Estadísticas
          </button>
          <button onClick={() => setTab("blog")} data-testid="tab-blog" className={`btn-pill text-sm ${tab === "blog" ? "btn-secondary" : "btn-ghost"}`}>
            <Newspaper size={16} /> Blog y noticias
          </button>
        </div>

        {tab === "leads" && <LeadsPanel authHeader={authHeader} onStatusChanged={refreshCounts} />}
        {tab === "reviews" && <ReviewsPanel authHeader={authHeader} />}
        {tab === "google" && <GoogleReviewsPanel authHeader={authHeader} />}
        {tab === "puntos" && <PointsPanel authHeader={authHeader} onStatusChanged={refreshCounts} />}
        {tab === "banner" && <NextCourseBannerPanel authHeader={authHeader} />}
        {tab === "schedule" && <CourseSchedulePanel authHeader={authHeader} />}
        {tab === "account" && <AccountPanel authHeader={authHeader} onPasswordChanged={logout} />}
        {tab === "stats" && <StatsPanel authHeader={authHeader} />}
        {tab === "blog" && <BlogPanel authHeader={authHeader} />}
      </div>
    </div>
  );
}

function LeadsPanel({ authHeader, onStatusChanged }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [waSubject, setWaSubject] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contact`, { headers: authHeader });
      setLeads(data);
    } catch {
      toast.error("No se pudieron cargar las solicitudes");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/contact/${leadId}/status`, { status }, { headers: authHeader });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status, deleted_at: status === "eliminado" ? new Date().toISOString() : null } : l)));
      toast.success(status === "eliminado" ? "Marcada como eliminada (se borrará en 15 días)" : "Estado actualizado");
      if (onStatusChanged) onStatusChanged();
    } catch {
      toast.error("No se pudo cambiar el estado");
    }
  };

  const updateContactAttempt = async (leadId, attempt) => {
    const prev = leads.find((l) => l.id === leadId);
    setLeads((rows) => rows.map((l) => (l.id === leadId ? { ...l, contact_attempt: attempt || null } : l)));
    try {
      await axios.patch(`${API}/contact/${leadId}/contact-attempt`, { contact_attempt: attempt || null }, { headers: authHeader });
      const label = attempt === "called" ? "Marcado: Ya llamé" : attempt === "no_answer" ? "Marcado: Sin respuesta" : "Intento borrado";
      toast.success(label);
    } catch {
      setLeads((rows) => rows.map((l) => (l.id === leadId ? { ...l, contact_attempt: prev?.contact_attempt || null } : l)));
      toast.error("No se pudo registrar el intento");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/contact/export`, { headers: authHeader, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads_faro_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Excel/CSV descargado");
    } catch {
      toast.error("Error al exportar");
    }
  };

  const visibleLeads = leads.filter((l) => showDeleted || l.status !== "eliminado");
  const visibleIds = visibleLeads.map((l) => l.id).filter(Boolean);
  const selectedVisible = visibleIds.filter((id) => selected.has(id));
  const allSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`¿Marcar ${ids.length} solicitud${ids.length === 1 ? "" : "es"} como eliminada${ids.length === 1 ? "" : "s"}? Se borrará${ids.length === 1 ? "" : "n"} definitivamente en 15 días.`)) return;
    setBulkBusy(true);
    const results = await Promise.allSettled(
      ids.map((id) => axios.patch(`${API}/contact/${id}/status`, { status: "eliminado" }, { headers: authHeader })),
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    if (ok) {
      const okIds = new Set(ids.filter((_, i) => results[i].status === "fulfilled"));
      setLeads((prev) => prev.map((l) => (okIds.has(l.id) ? { ...l, status: "eliminado", deleted_at: new Date().toISOString() } : l)));
    }
    clearSelection();
    setBulkBusy(false);
    if (fail === 0) toast.success(`${ok} solicitud${ok === 1 ? "" : "es"} marcada${ok === 1 ? "" : "s"} como eliminada${ok === 1 ? "" : "s"}`);
    else if (ok === 0) toast.error("No se pudo eliminar ninguna solicitud");
    else toast.warning(`${ok} eliminadas, ${fail} fallaron`);
    if (onStatusChanged) onStatusChanged();
  };

  return (
    <div data-testid="leads-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl">Solicitudes recibidas <span className="text-[var(--faro-text-soft)] font-bold text-lg">({visibleLeads.length})</span></h2>
        <div className="flex gap-2 items-center flex-wrap">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-[var(--faro-text-soft)]">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              data-testid="leads-show-deleted"
            />
            Ver eliminadas
          </label>
          <button onClick={load} data-testid="leads-refresh" className="btn-pill btn-ghost text-sm"><RefreshCw size={15} /> Actualizar</button>
          <button onClick={exportCsv} data-testid="export-csv" className="btn-pill btn-primary text-sm"><Download size={15} /> Exportar a Excel</button>
        </div>
      </div>

      {selected.size > 0 && (
        <div
          data-testid="leads-bulk-bar"
          className="mb-3 flex items-center justify-between gap-3 rounded-2xl bg-[var(--faro-blue)] text-white px-4 py-3 shadow-md"
        >
          <div className="text-sm font-bold">
            {selected.size} solicitud{selected.size === 1 ? "" : "es"} seleccionada{selected.size === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearSelection}
              data-testid="leads-bulk-clear"
              className="btn-pill bg-white/10 hover:bg-white/20 text-white text-xs"
            >
              Quitar selección
            </button>
            <button
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="leads-bulk-delete"
              className="btn-pill bg-[var(--faro-red)] hover:brightness-110 text-white text-xs disabled:opacity-60"
            >
              <Trash2 size={13} /> {bulkBusy ? "Eliminando…" : `Eliminar ${selected.size} seleccionada${selected.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white overflow-hidden">
        <DualScrollX testid="leads-dual-scroll">
          <table className="w-full text-sm">
            <thead className="bg-[var(--faro-bg)] text-left">
              <tr className="text-xs uppercase tracking-[0.1em] text-[var(--faro-blue)]">
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAllVisible}
                    aria-label="Seleccionar todas"
                    data-testid="leads-select-all"
                    className="h-4 w-4 cursor-pointer accent-[var(--faro-blue)]"
                  />
                </th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Teléfono</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Centro</th>
                <th className="px-4 py-3">Permiso</th>
                <th className="px-4 py-3">Mensaje</th>
                <th className="px-4 py-3 text-center">WhatsApp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(30,42,143,0.08)]">
              {loading && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-[var(--faro-text-soft)]">Cargando...</td></tr>
              )}
              {!loading && visibleLeads.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-[var(--faro-text-soft)]">Aún no hay solicitudes.</td></tr>
              )}
              {!loading && visibleLeads.map((l, i) => (
                <tr key={l.id || i} data-testid={`lead-row-${i}`} className={`hover:bg-[var(--faro-bg)] ${l.status === "eliminado" ? "opacity-50" : ""} ${selected.has(l.id) ? "bg-[rgba(30,42,143,0.06)]" : ""}`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(l.id)}
                      onChange={() => toggleOne(l.id)}
                      aria-label={`Seleccionar solicitud de ${l.name}`}
                      data-testid={`lead-select-${i}`}
                      className="h-4 w-4 cursor-pointer accent-[var(--faro-blue)]"
                    />
                  </td>
                  <td className="px-4 py-3 align-top" style={{ minWidth: 170 }}>
                    <StatusPicker value={l.status || "nuevo"} onChange={(s) => updateStatus(l.id, s)} testid={`lead-status-${i}`} />
                    <div className="mt-1.5">
                      <AdminNoteInline
                        endpoint={`${API}/contact/${l.id}/admin-note`}
                        value={l.admin_note || ""}
                        headers={authHeader}
                        onSaved={(v) => setLeads((rows) => rows.map((x) => (x.id === l.id ? { ...x, admin_note: v } : x)))}
                        testid={`lead-note-${i}`}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-[var(--faro-text-soft)]">{new Date(l.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="px-4 py-3 font-semibold">{l.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <a href={`tel:${l.phone}`} className="text-[var(--faro-blue)]" data-testid={`lead-phone-${i}`}>{l.phone}</a>
                      {l.no_whatsapp && (
                        <ContactAttemptChip
                          value={l.contact_attempt}
                          onChange={(v) => updateContactAttempt(l.id, v)}
                          testid={`lead-attempt-${i}`}
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[var(--faro-text-soft)]">{l.email || "—"}</td>
                  <td className="px-4 py-3">{CENTER_NAMES[l.center] || l.center}</td>
                  <td className="px-4 py-3">{l.permit_interest || "—"}</td>
                  <td className="px-4 py-3 max-w-xs truncate text-[var(--faro-text-soft)]" title={l.message}>{l.message || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => setWaSubject(l)}
                      disabled={!!l.no_whatsapp}
                      data-testid={`lead-wa-${i}`}
                      title={l.no_whatsapp ? "Este lead declaró no tener WhatsApp — llámalo o envíale SMS" : "Enviar recordatorio por WhatsApp"}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full text-white transition-transform hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                      style={{ background: l.no_whatsapp ? "#9ca3af" : "#25d366" }}
                    >
                      <MessageCircle size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DualScrollX>
      </div>
      <WhatsappReminderModal
        open={!!waSubject}
        onClose={() => setWaSubject(null)}
        subject={waSubject}
        templates={CONTACT_TEMPLATES}
      />
    </div>
  );
}

const EMPTY_REVIEW = { author: "", permit: "", rating: 5, text: "", published: true };

function ReviewsPanel({ authHeader }) {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState(EMPTY_REVIEW);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reviews/all`, { headers: authHeader });
      setReviews(data);
    } catch {
      toast.error("No se pudieron cargar las reseñas");
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.author || !form.text) { toast.error("Autor y texto son obligatorios"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/reviews`, { ...form, rating: Number(form.rating) }, { headers: authHeader });
      toast.success("Reseña añadida");
      setForm(EMPTY_REVIEW);
      load();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (r) => {
    try {
      await axios.put(`${API}/reviews/${r.id}`, { published: !r.published }, { headers: authHeader });
      load();
    } catch { toast.error("Error"); }
  };

  const remove = async (r) => {
    if (!window.confirm("¿Eliminar esta reseña?")) return;
    try {
      await axios.delete(`${API}/reviews/${r.id}`, { headers: authHeader });
      toast.success("Reseña eliminada");
      load();
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="reviews-panel">
      <form onSubmit={add} className="lg:col-span-4 rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white p-6 space-y-3 h-fit" data-testid="review-form">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Plus size={18} /> Nueva reseña</h3>
        <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="review-author" placeholder="Nombre del alumno" className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)]" />
        <input value={form.permit} onChange={(e) => setForm({ ...form, permit: e.target.value })} data-testid="review-permit" placeholder="Permiso (ej. B, A2)" className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)]" />
        <select value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="review-rating" className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none bg-white">
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} estrellas</option>)}
        </select>
        <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="review-text" rows={4} placeholder="Texto de la reseña" className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)]" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="review-published" />
          Publicar en la web
        </label>
        <button type="submit" disabled={saving} data-testid="review-submit" className="btn-pill btn-primary w-full justify-center disabled:opacity-60">
          {saving ? "Guardando..." : "Añadir reseña"}
        </button>
      </form>

      <div className="lg:col-span-8 space-y-3">
        <h3 className="font-display font-bold text-lg">Reseñas ({reviews.length})</h3>
        {reviews.map((r) => (
          <div key={r.id} data-testid={`admin-review-${r.id}`} className="rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{r.author}</span>
                  {r.permit && <span className="text-xs uppercase font-bold text-[var(--faro-red)]">· {r.permit}</span>}
                  <span className="flex">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={13} className="fill-[var(--faro-yellow)] text-[var(--faro-yellow)]" />)}</span>
                </div>
                <p className="text-sm text-[var(--faro-text-soft)] mt-1">{r.text}</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button onClick={() => togglePublish(r)} data-testid={`review-toggle-${r.id}`} title={r.published ? "Ocultar" : "Publicar"} className={`w-9 h-9 rounded-lg flex items-center justify-center ${r.published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                  {r.published ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button onClick={() => remove(r)} data-testid={`review-delete-${r.id}`} className="w-9 h-9 rounded-lg bg-red-50 text-[var(--faro-red)] flex items-center justify-center hover:bg-red-100">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function PointsPanel({ authHeader, onStatusChanged }) {
  const SOURCE_LABELS = {
    "/recuperacion-de-puntos-vigo": "Puntos",
    "/recuperacion-del-carnet-vigo": "Carnet",
    "/curso-sensibilizacion-vigo": "Sensibilización",
    "/": "Inicio",
  };
  const COURSE_BADGES = {
    A: { label: "A", cls: "bg-blue-100 text-blue-800" },
    B1: { label: "B1", cls: "bg-amber-100 text-amber-800" },
    B2: { label: "B2", cls: "bg-purple-100 text-purple-800" },
    B3: { label: "B3", cls: "bg-pink-100 text-pink-800" },
    ciclo: { label: "Ciclo Adicional", cls: "bg-teal-100 text-teal-800" },
    CICLO_ADICIONAL: { label: "Ciclo Adicional", cls: "bg-teal-100 text-teal-800" },
    no_seguro: { label: "NES", cls: "bg-gray-200 text-gray-700" },
    NO_SABE: { label: "Orientación", cls: "bg-orange-100 text-orange-800" },
  };
  const COURSE_TYPE_OPTIONS = [
    { value: "A", label: "A · Recuperar 4 pts", cls: "bg-blue-100 text-blue-800" },
    { value: "B1", label: "B1 · Perdió todos", cls: "bg-amber-100 text-amber-800" },
    { value: "B2", label: "B2 · Sentencia ≤2 años", cls: "bg-purple-100 text-purple-800" },
    { value: "B3", label: "B3 · Sentencia >2 años", cls: "bg-pink-100 text-pink-800" },
    { value: "CICLO_ADICIONAL", label: "Ciclo Adicional (4 h)", cls: "bg-teal-100 text-teal-800" },
    { value: "NO_SABE", label: "Orientación (no sabe)", cls: "bg-orange-100 text-orange-800" },
  ];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [waSubject, setWaSubject] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [courseFilter, setCourseFilter] = useState(""); // "" = todas las convocatorias
  const [originFilter, setOriginFilter] = useState("all"); // "all" | "school" | "online"
  const [schoolCenterFilter, setSchoolCenterFilter] = useState(""); // "" = todos los centros; sólo aplica con originFilter="school"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/points-registration`, { headers: authHeader });
      setRows(data);
    } catch {
      toast.error("No se pudieron cargar las inscripciones");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (regId, status) => {
    try {
      await axios.patch(`${API}/points-registration/${regId}/status`, { status }, { headers: authHeader });
      const nowIso = new Date().toISOString();
      setRows((prev) => prev.map((r) => {
        if (r.id !== regId) return r;
        const next = {
          ...r,
          status,
          deleted_at: status === "eliminado" ? nowIso : null,
        };
        if (status === "matriculado" && !r.matriculated_at) next.matriculated_at = nowIso;
        if (status !== "matriculado") next.matriculated_at = null;
        return next;
      }));
      toast.success(status === "eliminado" ? "Marcada como eliminada (se borrará en 15 días)" : "Estado actualizado");
      if (onStatusChanged) onStatusChanged();
    } catch {
      toast.error("No se pudo cambiar el estado");
    }
  };

  const updateCourseType = async (regId, course_type) => {
    const prevRow = rows.find((r) => r.id === regId);
    setRows((prev) => prev.map((r) => (r.id === regId ? { ...r, course_type } : r)));
    try {
      const { data } = await axios.patch(`${API}/points-registration/${regId}/course-type`, { course_type }, { headers: authHeader });
      // Backend now resolves the next scheduled slot (course_month, start, end) — reflect it in-place.
      setRows((prev) => prev.map((r) =>
        r.id === regId
          ? { ...r, course_type, course_month: data.course_month || "", course_start_date: data.course_start_date || null, course_end_date: data.course_end_date || null }
          : r
      ));
      toast.success("Tipo de curso actualizado");
    } catch {
      // rollback
      setRows((prev) => prev.map((r) => (r.id === regId ? { ...r, course_type: prevRow?.course_type || "" } : r)));
      toast.error("No se pudo cambiar el tipo de curso");
    }
  };

  const updateCourseMonth = async (regId, course_month) => {
    const prevRow = rows.find((r) => r.id === regId);
    setRows((prev) => prev.map((r) => (r.id === regId ? { ...r, course_month } : r)));
    try {
      const { data } = await axios.patch(
        `${API}/points-registration/${regId}/course-month`,
        { course_month },
        { headers: authHeader },
      );
      setRows((prev) => prev.map((r) =>
        r.id === regId
          ? { ...r, course_month: data.course_month || "", course_start_date: data.course_start_date || null, course_end_date: data.course_end_date || null }
          : r
      ));
      toast.success(course_month ? "Convocatoria actualizada" : "Convocatoria desasignada");
    } catch {
      setRows((prev) => prev.map((r) => (r.id === regId ? { ...r, course_month: prevRow?.course_month || "" } : r)));
      toast.error("No se pudo cambiar el mes del curso");
    }
  };

  const updateContactAttempt = async (regId, attempt) => {
    const prevRow = rows.find((r) => r.id === regId);
    setRows((prev) => prev.map((r) => (r.id === regId ? { ...r, contact_attempt: attempt || null } : r)));
    try {
      await axios.patch(
        `${API}/points-registration/${regId}/contact-attempt`,
        { contact_attempt: attempt || null },
        { headers: authHeader },
      );
      const label = attempt === "called" ? "Marcado: Ya llamé" : attempt === "no_answer" ? "Marcado: Sin respuesta" : "Intento borrado";
      toast.success(label);
    } catch {
      setRows((prev) => prev.map((r) => (r.id === regId ? { ...r, contact_attempt: prevRow?.contact_attempt || null } : r)));
      toast.error("No se pudo registrar el intento");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/points-registration/export`, { headers: authHeader, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `puntos_faro_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Excel/CSV descargado");
    } catch {
      toast.error("Error al exportar");
    }
  };

  const visibleRows = sortPointsRows(
    rows
      .filter((r) => showDeleted || r.status !== "eliminado")
      .filter((r) => !courseFilter || courseMonthLabel(r.course_type, r.course_month) === courseFilter)
      .filter((r) => (
        originFilter === "school" ? !!r.filled_by_school
        : originFilter === "online" ? !r.filled_by_school
        : true
      ))
      .filter((r) => (
        originFilter === "school" && schoolCenterFilter
          ? (r.school_center || "") === schoolCenterFilter
          : true
      )),
  );
  // Contadores rápidos para el toggle Origen (siempre sobre filas visibles según showDeleted + courseFilter, ignoran el propio origin filter)
  const originCounts = useMemo(() => {
    const base = rows
      .filter((r) => showDeleted || r.status !== "eliminado")
      .filter((r) => !courseFilter || courseMonthLabel(r.course_type, r.course_month) === courseFilter);
    let s = 0, o = 0;
    for (const r of base) (r.filled_by_school ? s++ : o++);
    return { all: base.length, school: s, online: o };
  }, [rows, showDeleted, courseFilter]);
  // Opciones y conteos por centro cuando se está filtrando por "Autoescuela"
  const schoolCenterOptions = useMemo(() => {
    const base = rows
      .filter((r) => showDeleted || r.status !== "eliminado")
      .filter((r) => !courseFilter || courseMonthLabel(r.course_type, r.course_month) === courseFilter)
      .filter((r) => !!r.filled_by_school);
    const map = new Map();
    for (const r of base) {
      const key = r.school_center || "";
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()]
      .filter(([k, v]) => v > 0 || k === schoolCenterFilter)
      .sort((a, b) => b[1] - a[1])
      .map(([id, count]) => ({ id, count, label: id ? (CENTER_NAMES[id] || id) : "Sin centro" }));
  }, [rows, showDeleted, courseFilter, schoolCenterFilter]);
  // Lista única de convocatorias existentes, contando solo filas visibles según `showDeleted`.
  // Se oculta cualquier convocatoria con 0 inscripciones (salvo si es la actualmente filtrada).
  const courseFilterOptions = useMemo(() => {
    const map = new Map(); // label -> { count, month }
    for (const r of rows) {
      const label = courseMonthLabel(r.course_type, r.course_month);
      if (!label) continue;
      if (!showDeleted && r.status === "eliminado") continue;
      const cur = map.get(label) || { count: 0, month: r.course_month || "" };
      cur.count += 1;
      map.set(label, cur);
    }
    return [...map.entries()]
      .filter(([label, v]) => v.count > 0 || label === courseFilter)
      .sort((a, b) => String(a[1].month).localeCompare(String(b[1].month)))
      .map(([label, v]) => ({ label, count: v.count }));
  }, [rows, showDeleted, courseFilter]);
  const visibleIds = visibleRows.map((r) => r.id).filter(Boolean);
  const selectedVisible = visibleIds.filter((id) => selected.has(id));
  const allSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`¿Marcar ${ids.length} inscripción${ids.length === 1 ? "" : "es"} como eliminada${ids.length === 1 ? "" : "s"}? Se borrará${ids.length === 1 ? "" : "n"} definitivamente en 15 días.`)) return;
    setBulkBusy(true);
    const results = await Promise.allSettled(
      ids.map((id) => axios.patch(`${API}/points-registration/${id}/status`, { status: "eliminado" }, { headers: authHeader })),
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    if (ok) {
      const okIds = new Set(ids.filter((_, i) => results[i].status === "fulfilled"));
      setRows((prev) => prev.map((r) => (okIds.has(r.id) ? { ...r, status: "eliminado", deleted_at: new Date().toISOString() } : r)));
    }
    clearSelection();
    setBulkBusy(false);
    if (fail === 0) toast.success(`${ok} inscripción${ok === 1 ? "" : "es"} marcada${ok === 1 ? "" : "s"} como eliminada${ok === 1 ? "" : "s"}`);
    else if (ok === 0) toast.error("No se pudo eliminar ninguna inscripción");
    else toast.warning(`${ok} eliminadas, ${fail} fallaron`);
    if (onStatusChanged) onStatusChanged();
  };

  return (
    <div data-testid="points-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl">Inscripciones puntos <span className="text-[var(--faro-text-soft)] font-bold text-lg">({visibleRows.length})</span></h2>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="inline-flex items-center rounded-full border border-[rgba(30,42,143,0.2)] bg-white overflow-hidden" role="group" aria-label="Filtrar por origen" data-testid="points-origin-filter">
            {[
              { v: "all", label: `Todos (${originCounts.all})`, cls: "" },
              { v: "school", label: `Autoescuela (${originCounts.school})`, cls: "" },
              { v: "online", label: `Online (${originCounts.online})`, cls: "" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => { setOriginFilter(opt.v); if (opt.v !== "school") setSchoolCenterFilter(""); clearSelection(); }}
                data-testid={`points-origin-filter-${opt.v}`}
                aria-pressed={originFilter === opt.v}
                className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 transition-colors ${
                  originFilter === opt.v
                    ? "bg-[var(--faro-blue)] text-white"
                    : "text-[var(--faro-blue)] hover:bg-[var(--faro-bg)]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {originFilter === "school" && schoolCenterOptions.length > 0 && (
            <select
              value={schoolCenterFilter}
              onChange={(e) => { setSchoolCenterFilter(e.target.value); clearSelection(); }}
              data-testid="points-school-center-filter"
              aria-label="Filtrar por centro autoescuela"
              className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[rgba(30,42,143,0.2)] bg-white text-[var(--faro-blue)] cursor-pointer hover:border-[var(--faro-blue)]"
            >
              <option value="">Todos los centros ({originCounts.school})</option>
              {schoolCenterOptions.map((opt) => (
                <option key={opt.id || "sin"} value={opt.id}>{opt.label} ({opt.count})</option>
              ))}
            </select>
          )}
          {courseFilterOptions.length > 0 && (
            <select
              value={courseFilter}
              onChange={(e) => { setCourseFilter(e.target.value); clearSelection(); }}
              data-testid="points-course-filter"
              aria-label="Filtrar por convocatoria"
              className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[rgba(30,42,143,0.2)] bg-white text-[var(--faro-blue)] cursor-pointer hover:border-[var(--faro-blue)]"
            >
              <option value="">Todas las convocatorias ({rows.filter((r) => showDeleted || r.status !== "eliminado").length})</option>
              {courseFilterOptions.map((opt) => (
                <option key={opt.label} value={opt.label}>{opt.label} ({opt.count})</option>
              ))}
            </select>
          )}
          <label className="flex items-center gap-1.5 text-xs font-semibold text-[var(--faro-text-soft)]">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              data-testid="points-show-deleted"
            />
            Ver eliminadas
          </label>
          <button onClick={load} data-testid="points-refresh" className="btn-pill btn-ghost text-sm"><RefreshCw size={15} /> Actualizar</button>
          <button onClick={exportCsv} data-testid="points-export-csv" className="btn-pill btn-primary text-sm"><Download size={15} /> Exportar a Excel</button>
        </div>
      </div>

      {selected.size > 0 && (
        <div
          data-testid="points-bulk-bar"
          className="mb-3 flex items-center justify-between gap-3 rounded-2xl bg-[var(--faro-blue)] text-white px-4 py-3 shadow-md"
        >
          <div className="text-sm font-bold">
            {selected.size} inscripción{selected.size === 1 ? "" : "es"} seleccionada{selected.size === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearSelection}
              data-testid="points-bulk-clear"
              className="btn-pill bg-white/10 hover:bg-white/20 text-white text-xs"
            >
              Quitar selección
            </button>
            <button
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="points-bulk-delete"
              className="btn-pill bg-[var(--faro-red)] hover:brightness-110 text-white text-xs disabled:opacity-60"
            >
              <Trash2 size={13} /> {bulkBusy ? "Eliminando…" : `Eliminar ${selected.size} seleccionada${selected.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white overflow-hidden">
        <DualScrollX testid="points-dual-scroll">
          <table className="w-full text-sm [&_th]:px-3 [&_th]:py-3 [&_td]:px-3 [&_td]:py-3 xl:[&_th]:px-2 xl:[&_td]:px-2 xl:text-[13px]" data-testid="points-table">
            <thead className="bg-[var(--faro-bg)] text-left">
              <tr className="text-xs uppercase tracking-[0.1em] text-[var(--faro-blue)]">
                <th className="w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAllVisible}
                    aria-label="Seleccionar todas"
                    data-testid="points-select-all"
                    className="h-4 w-4 cursor-pointer accent-[var(--faro-blue)]"
                  />
                </th>
                <th>Estado</th>
                <th>Fecha</th>
                <th>Curso</th>
                <th>Origen</th>
                <th>Nombre y apellidos</th>
                <th>DNI/NIE</th>
                <th>Teléfono</th>
                <th>Dirección</th>
                <th>CP</th>
                <th>Localidad</th>
                <th>Provincia</th>
                <th>F. nac.</th>
                <th>1er carnet</th>
                <th>Email</th>
                <th>C/Bus</th>
                <th>Comentarios</th>
                <th className="text-center">WhatsApp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(30,42,143,0.08)]">
              {loading && (
                <tr><td colSpan={18} className="!py-8 text-center text-[var(--faro-text-soft)]">Cargando...</td></tr>
              )}
              {!loading && visibleRows.length === 0 && (
                <tr><td colSpan={18} className="!py-8 text-center text-[var(--faro-text-soft)]">Aún no hay inscripciones.</td></tr>
              )}
              {!loading && visibleRows.map((r, i) => (
                <tr key={r.id || i} data-testid={`points-row-${i}`} className={`hover:bg-[var(--faro-bg)] ${r.status === "eliminado" ? "opacity-50" : ""} ${selected.has(r.id) ? "bg-[rgba(30,42,143,0.06)]" : ""}`}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleOne(r.id)}
                      aria-label={`Seleccionar inscripción de ${r.full_name}`}
                      data-testid={`points-select-${i}`}
                      className="h-4 w-4 cursor-pointer accent-[var(--faro-blue)]"
                    />
                  </td>
                  <td className="align-top" style={{ minWidth: 170 }}>
                    <StatusPicker value={r.status || "nuevo"} onChange={(s) => updateStatus(r.id, s)} testid={`points-status-${i}`} options={POINTS_STATUS_OPTIONS} />
                    <div className="mt-1.5">
                      <AdminNoteInline
                        endpoint={`${API}/points-registration/${r.id}/admin-note`}
                        value={r.admin_note || ""}
                        headers={authHeader}
                        onSaved={(v) => setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, admin_note: v } : x)))}
                        testid={`points-note-${i}`}
                      />
                    </div>
                  </td>
                  <td className="whitespace-nowrap text-[var(--faro-text-soft)]">
                    <div>{new Date(r.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}</div>
                    {r.course_type && r.course_type !== "NO_SABE" && r.course_type !== "no_seguro" && r.course_type !== "CICLO_ADICIONAL" && r.course_type !== "ciclo" && (
                      <div className="mt-0.5 flex items-center gap-1" data-testid={`points-course-month-${i}`}>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--faro-blue)]">{r.course_type}</span>
                        <select
                          value={r.course_month || ""}
                          onChange={(e) => updateCourseMonth(r.id, e.target.value)}
                          data-testid={`points-course-month-select-${i}`}
                          aria-label="Cambiar mes del curso"
                          title="Cambiar mes del curso"
                          className="text-[10px] font-bold uppercase tracking-wider text-[var(--faro-blue)] bg-transparent border-0 border-b border-dashed border-[var(--faro-blue)]/30 hover:border-[var(--faro-blue)] focus:outline-none focus:border-[var(--faro-blue)] cursor-pointer py-0 pr-1 appearance-none"
                        >
                          <option value="">— sin mes —</option>
                          {buildMonthOptions(r.course_month).map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap" data-testid={`points-course-${i}`}>
                    <StatusPicker
                      value={
                        r.course_type === "ciclo"
                          ? "CICLO_ADICIONAL"
                          : r.course_type === "no_seguro"
                          ? "NO_SABE"
                          : r.course_type || ""
                      }
                      onChange={(v) => updateCourseType(r.id, v)}
                      testid={`points-course-select-${i}`}
                      options={
                        r.course_type
                          ? COURSE_TYPE_OPTIONS
                          : [{ value: "", label: "— sin asignar", cls: "bg-gray-100 text-gray-500" }, ...COURSE_TYPE_OPTIONS]
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap" data-testid={`points-source-${i}`}>
                    <div className="flex flex-col gap-1">
                      {r.filled_by_school ? (
                        <span
                          data-testid={`points-origin-school-${i}`}
                          title={r.school_center ? `Cubierto por la secretaría en ${CENTER_NAMES[r.school_center] || r.school_center}` : "Cubierto por la secretaría (centro sin especificar)"}
                          className="inline-flex items-center gap-1 rounded-full bg-[var(--faro-blue)] text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 w-fit"
                        >
                          {r.school_center ? `Autoescuela · ${CENTER_NAMES[r.school_center] || r.school_center}` : "Autoescuela"}
                        </span>
                      ) : (
                        <span
                          data-testid={`points-origin-online-${i}`}
                          title="Cubierto por el alumno online"
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 w-fit"
                        >
                          Online
                        </span>
                      )}
                      <span className="text-[11px] text-[var(--faro-text-soft)]">{r.source_page ? (SOURCE_LABELS[r.source_page] || r.source_page) : "—"}</span>
                    </div>
                  </td>
                  <td className="font-semibold">{r.full_name}</td>
                  <td className="whitespace-nowrap select-all">{r.dni_nie}</td>
                  <td className="whitespace-nowrap">
                    {r.phone ? (
                      r.no_whatsapp ? (
                        <div className="flex flex-col gap-0.5">
                          <span data-testid={`points-phone-nowa-${i}`} className="text-[var(--faro-text)] select-all font-semibold">{r.phone}</span>
                          <ContactAttemptChip
                            value={r.contact_attempt}
                            onChange={(v) => updateContactAttempt(r.id, v)}
                            testid={`points-attempt-${i}`}
                          />
                        </div>
                      ) : (
                        <a
                          href={`https://wa.me/${normalizePhoneForWa(r.phone)}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Enviar WhatsApp"
                          aria-label={`Enviar WhatsApp a ${r.phone}`}
                          data-testid={`points-phone-wa-${i}`}
                          className="text-[var(--faro-blue)] hover:text-[var(--faro-red)] select-all inline-flex items-center gap-1"
                        >
                          {r.phone}
                        </a>
                      )
                    ) : "—"}
                  </td>
                  <td className="select-all" title={r.address}>{r.address}</td>
                  <td className="whitespace-nowrap select-all">{r.postal_code}</td>
                  <td className="whitespace-nowrap" data-testid={`points-city-${i}`}>{r.city || "—"}</td>
                  <td className="whitespace-nowrap" data-testid={`points-province-${i}`}>{r.province || "—"}</td>
                  <td className="whitespace-nowrap select-all">{r.birth_date}</td>
                  <td className="whitespace-nowrap select-all">{r.first_license_date || "—"}</td>
                  <td className="text-[var(--faro-text-soft)] select-all break-all">{r.email || "—"}</td>
                  <td className="whitespace-nowrap">{yesNoLabel(r.has_heavy_license)}</td>
                  <td className="max-w-[200px]" data-testid={`points-comments-${i}`}>
                    {r.comments ? <span className="block" title={r.comments}>{r.comments}</span> : "—"}
                  </td>
                  <td className="text-center">
                    <button
                      onClick={() => setWaSubject({ ...r, center: "plazaamerica" })}
                      disabled={!!r.no_whatsapp}
                      data-testid={`points-wa-${i}`}
                      title={r.no_whatsapp ? "Este alumno declaró no tener WhatsApp — llámalo o envíale SMS" : "Enviar recordatorio por WhatsApp"}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full text-white transition-transform hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                      style={{ background: r.no_whatsapp ? "#9ca3af" : "#25d366" }}
                    >
                      <MessageCircle size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DualScrollX>
      </div>
      <WhatsappReminderModal
        open={!!waSubject}
        onClose={() => setWaSubject(null)}
        subject={waSubject}
        templates={POINTS_TEMPLATES}
      />
    </div>
  );
}


function NextCourseBannerPanel({ authHeader }) {
  const emptyCourse = () => ({
    _key: `c_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    course_label: "",
    subtitle: "",
    start_date: "",
    seats_remaining: "",
    cta_href: "#contacto",
    cta_label: "Reservar mi plaza",
  });
  const [enabled, setEnabled] = useState(false);
  const [courses, setCourses] = useState([emptyCourse()]);
  const [rotateSeconds, setRotateSeconds] = useState(2);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      axios.get(`${API}/next-course`),
      axios.get(`${API}/config`).catch(() => ({ data: {} })),
    ]).then(([nc, cfg]) => {
      if (!alive) return;
      const data = nc.data;
      setEnabled(!!data.enabled);
      const list = Array.isArray(data.courses) && data.courses.length ? data.courses : [emptyCourse()];
      setCourses(list.map((c, i) => ({
        _key: `c_load_${i}`,
        course_label: c.course_label || "",
        subtitle: c.subtitle || "",
        start_date: (c.start_date || "").slice(0, 16),
        seats_remaining: c.seats_remaining ?? "",
        cta_href: c.cta_href || "#contacto",
        cta_label: c.cta_label || "Reservar mi plaza",
      })));
      const s = parseInt(cfg?.data?.banner_rotate_seconds, 10);
      if (Number.isFinite(s) && s > 0) setRotateSeconds(s);
    }).catch(() => {}).finally(() => setLoading(false));
    return () => { alive = false; };
  }, []);

  const updateCourse = (i, patch) => {
    setCourses((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  };
  const addCourse = () => setCourses((prev) => (prev.length >= 6 ? prev : [...prev, emptyCourse()]));
  const removeCourse = (i) => setCourses((prev) => prev.filter((_, idx) => idx !== i));

  const save = async (e) => {
    e.preventDefault();
    const cleaned = courses
      .map((c) => ({
        course_label: c.course_label.trim(),
        subtitle: c.subtitle.trim() || null,
        start_date: c.start_date.length === 16 ? `${c.start_date}:00` : c.start_date,
        seats_remaining: c.seats_remaining === "" ? null : Number(c.seats_remaining),
        cta_href: c.cta_href.trim() || "#contacto",
        cta_label: c.cta_label.trim() || "Reservar mi plaza",
      }))
      .filter((c) => c.course_label && c.start_date);

    if (enabled && cleaned.length === 0) {
      toast.error("Añade al menos un curso con nombre y fecha, o desactiva el banner");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        enabled,
        courses: cleaned.map((c) => {
          const out = { ...c };
          if (out.subtitle === null) delete out.subtitle;
          if (out.seats_remaining === null) delete out.seats_remaining;
          return out;
        }),
      };
      const rot = Math.min(60, Math.max(1, parseInt(rotateSeconds, 10) || 2));
      await Promise.all([
        axios.put(`${API}/next-course`, payload, { headers: authHeader }),
        axios.put(`${API}/admin/config`, { banner_rotate_seconds: rot }, { headers: authHeader }),
      ]);
      setRotateSeconds(rot);
      toast.success("Banner actualizado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-[var(--faro-text-soft)]">Cargando...</div>;

  return (
    <div data-testid="banner-panel">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <h2 className="font-display font-black text-2xl">Banner &ldquo;Próximo curso&rdquo;</h2>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            data-testid="banner-enabled"
          />
          Mostrar banner en la portada
        </label>
      </div>

      <div className="mb-4 bg-white rounded-2xl border border-[rgba(30,42,143,0.12)] p-5 flex items-center gap-4 flex-wrap max-w-3xl" data-testid="banner-rotate-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)]">Rotación del banner</div>
          <div className="text-sm text-[var(--faro-text-soft)] mt-1">Tiempo que se muestra cada curso antes de pasar al siguiente. Valores útiles: 2-5 s.</div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="1"
            max="60"
            step="1"
            value={rotateSeconds}
            onChange={(e) => setRotateSeconds(e.target.value)}
            data-testid="banner-rotate-seconds"
            className="w-20 rounded-xl border border-[rgba(30,42,143,0.15)] px-3 py-2 outline-none focus:border-[var(--faro-blue)] text-center font-bold"
          />
          <span className="text-sm font-bold text-[var(--faro-text-soft)]">seg.</span>
        </div>
      </div>

      <form onSubmit={save} className="space-y-4 max-w-3xl">
        {courses.map((c, i) => (
          <div key={c._key} data-testid={`banner-course-${i}`} className="bg-white rounded-2xl border border-[rgba(30,42,143,0.12)] p-5 space-y-4 relative">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--faro-red)]">Curso {i + 1}</div>
              {courses.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeCourse(i)}
                  data-testid={`banner-remove-${i}`}
                  className="w-8 h-8 rounded-lg bg-red-50 text-[var(--faro-red)] flex items-center justify-center hover:bg-red-100"
                  aria-label="Eliminar curso"
                >
                  <XIcon size={16} />
                </button>
              )}
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Nombre del curso *</label>
              <input
                type="text"
                value={c.course_label}
                onChange={(e) => updateCourse(i, { course_label: e.target.value })}
                data-testid={`banner-course-label-${i}`}
                placeholder="Ej. CAP Inicial · Noviembre"
                className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)]"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Subtítulo (opcional)</label>
              <input
                type="text"
                value={c.subtitle}
                onChange={(e) => updateCourse(i, { subtitle: e.target.value })}
                data-testid={`banner-subtitle-${i}`}
                placeholder="Ej. 130h teóricas + prácticas · Coruxo"
                className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Fecha y hora de inicio *</label>
                <input
                  type="datetime-local"
                  value={c.start_date}
                  onChange={(e) => updateCourse(i, { start_date: e.target.value })}
                  data-testid={`banner-start-date-${i}`}
                  className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Plazas restantes</label>
                <input
                  type="number"
                  min="0"
                  value={c.seats_remaining}
                  onChange={(e) => updateCourse(i, { seats_remaining: e.target.value })}
                  data-testid={`banner-seats-${i}`}
                  placeholder="Vacío = ocultar"
                  className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Texto del botón</label>
                <input
                  type="text"
                  value={c.cta_label}
                  onChange={(e) => updateCourse(i, { cta_label: e.target.value })}
                  data-testid={`banner-cta-label-${i}`}
                  className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Destino del botón</label>
                <input
                  type="text"
                  value={c.cta_href}
                  onChange={(e) => updateCourse(i, { cta_href: e.target.value })}
                  data-testid={`banner-cta-href-${i}`}
                  placeholder="#contacto o URL completa"
                  className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)]"
                />
              </div>
            </div>
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={addCourse}
            disabled={courses.length >= 6}
            data-testid="banner-add-course"
            className="btn-pill btn-ghost text-sm disabled:opacity-50"
          >
            <Plus size={15} /> Añadir otro curso
          </button>
          <button type="submit" disabled={saving} data-testid="banner-save" className="btn-pill btn-primary justify-center disabled:opacity-60">
            <Save size={15} /> {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>

        <p className="text-xs text-[var(--faro-text-soft)]">
          Puedes añadir hasta 6 cursos. Los visitantes verán un carrusel con contador en tiempo real que rota cada 6s (pausable al pasar el ratón). Se oculta si desmarcas &ldquo;Mostrar banner&rdquo;.
        </p>
      </form>
    </div>
  );
}

function AccountPanel({ authHeader, onPasswordChanged }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const validate = () => {
    if (!form.current_password) return "Introduce tu contraseña actual";
    if (form.new_password.length < 8) return "La nueva contraseña debe tener al menos 8 caracteres";
    if (!/[A-Za-z]/.test(form.new_password) || !/\d/.test(form.new_password)) {
      return "La nueva contraseña debe incluir al menos una letra y un número";
    }
    if (form.new_password === form.current_password) return "La nueva contraseña debe ser distinta a la actual";
    if (form.new_password !== form.confirm_password) return "La confirmación no coincide con la nueva contraseña";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: form.current_password, new_password: form.new_password },
        { headers: authHeader }
      );
      toast.success("Contraseña actualizada. Vuelve a iniciar sesión.");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
      // Force re-login for safety (invalidates in-memory state; token still valid until it expires)
      setTimeout(() => { onPasswordChanged && onPasswordChanged(); }, 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cambiar la contraseña");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)] font-mono";

  return (
    <div data-testid="account-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-black text-2xl">Mi cuenta</h2>
      </div>

      <form onSubmit={submit} className="bg-white rounded-2xl border border-[rgba(30,42,143,0.12)] p-6 space-y-4 max-w-xl">
        <div className="flex items-center gap-3 mb-2 p-3 rounded-xl bg-[var(--faro-bg)]">
          <div className="w-10 h-10 rounded-full bg-[var(--faro-blue)] text-white flex items-center justify-center">
            <ShieldCheck size={18} />
          </div>
          <div className="text-sm">
            <div className="font-bold">Cambiar contraseña</div>
            <div className="text-[var(--faro-text-soft)]">Mínimo 8 caracteres, con al menos una letra y un número.</div>
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Contraseña actual *</label>
          <input
            type={showPw ? "text" : "password"}
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            data-testid="account-current-password"
            autoComplete="current-password"
            className={inputCls}
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Nueva contraseña *</label>
          <input
            type={showPw ? "text" : "password"}
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            data-testid="account-new-password"
            autoComplete="new-password"
            className={inputCls}
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Confirmar nueva contraseña *</label>
          <input
            type={showPw ? "text" : "password"}
            value={form.confirm_password}
            onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
            data-testid="account-confirm-password"
            autoComplete="new-password"
            className={inputCls}
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showPw}
            onChange={(e) => setShowPw(e.target.checked)}
            data-testid="account-show-pw"
          />
          Mostrar contraseñas
        </label>

        <button type="submit" disabled={saving} data-testid="account-submit" className="btn-pill btn-primary justify-center disabled:opacity-60">
          <KeyRound size={15} /> {saving ? "Guardando..." : "Actualizar contraseña"}
        </button>

        <p className="text-xs text-[var(--faro-text-soft)]">
          Tras el cambio, se cerrará tu sesión y deberás volver a entrar con la nueva contraseña.
        </p>
      </form>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color = "blue", testid }) {
  const colorCls = {
    blue: "bg-[var(--faro-blue)] text-white",
    red: "bg-[var(--faro-red)] text-white",
    yellow: "bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)]",
    green: "bg-green-600 text-white",
  }[color] || "bg-[var(--faro-blue)] text-white";
  return (
    <div className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-4 flex items-center gap-3" data-testid={testid}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${colorCls}`}>
        {Icon ? <Icon size={20} /> : null}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--faro-blue)]">{label}</div>
        <div className="font-display font-black text-3xl leading-none tabular-nums mt-0.5">{value}</div>
        {sub && <div className="text-xs text-[var(--faro-text-soft)] mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function CenterBar({ label, count, max, color }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-40 text-sm font-semibold truncate">{label}</div>
      <div className="flex-1 h-3 rounded-full bg-[var(--faro-bg)] overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="w-10 text-right text-sm font-black tabular-nums">{count}</div>
    </div>
  );
}

function StatsPanel({ authHeader }) {
  const [stats, setStats] = useState(null);
  const [gaData, setGaData] = useState({ configured: false });
  const [gaDays, setGaDays] = useState(7);
  const [config, setConfig] = useState({ ga_measurement_id: "", ga_dashboard_url: "" });
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeader }),
        axios.get(`${API}/admin/config`, { headers: authHeader }),
      ]);
      setStats(s.data);
      setConfig({ ga_measurement_id: c.data.ga_measurement_id || "", ga_dashboard_url: c.data.ga_dashboard_url || "" });
    } catch {
      toast.error("No se pudieron cargar las estadísticas");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  const loadGa = useCallback(async (days) => {
    try {
      const { data } = await axios.get(`${API}/admin/stats/ga?days=${days}`, { headers: authHeader });
      setGaData(data);
    } catch {
      setGaData({ configured: false, error: true });
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadGa(gaDays); }, [loadGa, gaDays]);

  const saveConfig = async (e) => {
    e.preventDefault();
    setSavingCfg(true);
    try {
      await axios.put(`${API}/admin/config`, config, { headers: authHeader });
      toast.success("Configuración guardada. Recarga la web pública para activar Google Analytics.");
    } catch {
      toast.error("No se pudo guardar la configuración");
    } finally {
      setSavingCfg(false);
    }
  };

  if (loading || !stats) {
    return <div className="text-[var(--faro-text-soft)]" data-testid="stats-loading">Cargando estadísticas...</div>;
  }

  const centerLabels = stats.center_labels || {};
  const centersOrder = ["coruxo", "panxon", "nigran", "acaniza", "plazaamerica"];
  const maxLead = Math.max(1, ...centersOrder.map((c) => (stats.leads.by_center_30[c] || 0)));
  const maxWa = Math.max(1, ...centersOrder.map((c) => (stats.events.whatsapp_by_center_30[c] || 0)));
  const maxPh = Math.max(1, ...centersOrder.map((c) => (stats.events.phone_by_center_30[c] || 0)));
  const pointsBySchool = stats.points?.by_school_center_30 || {};
  const maxPointsSchool = Math.max(1, ...centersOrder.map((c) => (pointsBySchool[c] || 0)));
  const totalPointsSchool = centersOrder.reduce((sum, c) => sum + (pointsBySchool[c] || 0), 0);
  const events30 = stats.events.by_type_30 || {};
  const events7 = stats.events.by_type_7 || {};

  const gaLink = config.ga_dashboard_url || (config.ga_measurement_id ? "https://analytics.google.com/analytics/web/" : "");

  return (
    <div data-testid="stats-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl">Estadísticas</h2>
        <button onClick={load} data-testid="stats-refresh" className="btn-pill btn-ghost text-sm"><RefreshCw size={15} /> Actualizar</button>
      </div>

      <div className="text-xs uppercase tracking-[0.2em] font-black text-[var(--faro-red)] mt-2 mb-3">Últimos 7 días</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Solicitudes" value={stats.leads.last_7_days} icon={Inbox} color="blue" testid="kpi-leads-7" />
        <KpiCard label="Inscripc. puntos" value={stats.points.last_7_days} icon={ShieldCheck} color="red" testid="kpi-points-7" />
        <KpiCard label="Clics WhatsApp" value={events7.whatsapp || 0} icon={MessageCircle} color="green" testid="kpi-wa-7" />
        <KpiCard label="Clics teléfono" value={events7.phone || 0} icon={Phone} color="yellow" testid="kpi-phone-7" />
      </div>

      <div className="text-xs uppercase tracking-[0.2em] font-black text-[var(--faro-red)] mt-6 mb-3">Últimos 30 días</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Solicitudes" value={stats.leads.last_30_days} sub={`Conversión ${stats.leads.conversion_rate_30}%`} icon={Inbox} color="blue" testid="kpi-leads-30" />
        <KpiCard label="Inscripc. puntos" value={stats.points.last_30_days} icon={ShieldCheck} color="red" testid="kpi-points-30" />
        <KpiCard label="Clics WhatsApp" value={events30.whatsapp || 0} icon={MessageCircle} color="green" testid="kpi-wa-30" />
        <KpiCard label="Clics teléfono" value={events30.phone || 0} icon={Phone} color="yellow" testid="kpi-phone-30" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-8">
        <div className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5">
          <h3 className="font-display font-black text-lg mb-1">Solicitudes por centro (30 días)</h3>
          <p className="text-xs text-[var(--faro-text-soft)] mb-4">Envíos del formulario de contacto por sede.</p>
          <div className="space-y-2">
            {centersOrder.map((cid) => (
              <CenterBar
                key={cid}
                label={centerLabels[cid] || cid}
                count={stats.leads.by_center_30[cid] || 0}
                max={maxLead}
                color="#1e2a8f"
              />
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5">
          <h3 className="font-display font-black text-lg mb-1">Estado de solicitudes (30 días)</h3>
          <p className="text-xs text-[var(--faro-text-soft)] mb-4">Cómo evolucionan tus leads.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-[var(--faro-bg)] p-4">
              <div className="text-[10px] uppercase tracking-wider font-black text-[var(--faro-blue)]">Nuevo</div>
              <div className="font-display font-black text-2xl tabular-nums">{stats.leads.by_status_30.nuevo || 0}</div>
            </div>
            <div className="rounded-xl bg-green-50 p-4">
              <div className="text-[10px] uppercase tracking-wider font-black text-green-800">Contactado</div>
              <div className="font-display font-black text-2xl tabular-nums text-green-800">{stats.leads.by_status_30.contactado || 0}</div>
            </div>
            <div className="rounded-xl bg-gray-100 p-4">
              <div className="text-[10px] uppercase tracking-wider font-black text-gray-700">Perdido</div>
              <div className="font-display font-black text-2xl tabular-nums text-gray-700">{stats.leads.by_status_30.perdido || 0}</div>
            </div>
            <div className="rounded-xl bg-red-50 p-4">
              <div className="text-[10px] uppercase tracking-wider font-black text-[var(--faro-red)]">Eliminado</div>
              <div className="font-display font-black text-2xl tabular-nums text-[var(--faro-red)]">{stats.leads.by_status_30.eliminado || 0}</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm">
            <TrendingUp size={16} className="text-[var(--faro-blue)]" />
            <span className="font-semibold">Tasa de conversión:</span>
            <span className="font-black text-[var(--faro-blue-deep)]" data-testid="stats-conversion">{stats.leads.conversion_rate_30}%</span>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5" data-testid="stats-points-by-school-center">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-display font-black text-lg">Informaciones de puntos por autoescuela (30 días)</h3>
          </div>
          <p className="text-xs text-[var(--faro-text-soft)] mb-4">
            Ranking de inscripciones al curso de puntos cubiertas por la secretaría, por centro.
            <span className="ml-1 font-bold text-[var(--faro-blue)]">Total: {totalPointsSchool}</span>
          </p>
          <div className="space-y-2">
            {centersOrder
              .map((cid) => ({ cid, count: pointsBySchool[cid] || 0 }))
              .sort((a, b) => b.count - a.count)
              .map(({ cid, count }, idx) => (
                <CenterBar
                  key={cid}
                  label={`${idx === 0 && count > 0 ? "🥇 " : idx === 1 && count > 0 ? "🥈 " : idx === 2 && count > 0 ? "🥉 " : ""}${centerLabels[cid] || cid}`}
                  count={count}
                  max={maxPointsSchool}
                  color="#1e2a8f"
                />
              ))}
          </div>
          {totalPointsSchool === 0 && (
            <p className="mt-3 text-xs text-[var(--faro-text-soft)] italic">
              Aún no hay inscripciones marcadas como "cubierto por la autoescuela" en los últimos 30 días.
            </p>
          )}
        </div>

        <div className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5">
          <h3 className="font-display font-black text-lg mb-1">WhatsApp por centro (30 días)</h3>
          <p className="text-xs text-[var(--faro-text-soft)] mb-4">Qué sede recibe más contactos por WhatsApp.</p>
          <div className="space-y-2">
            {centersOrder.map((cid) => (
              <CenterBar
                key={cid}
                label={centerLabels[cid] || cid}
                count={stats.events.whatsapp_by_center_30[cid] || 0}
                max={maxWa}
                color="#25d366"
              />
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5">
          <h3 className="font-display font-black text-lg mb-1">Llamadas por centro (30 días)</h3>
          <p className="text-xs text-[var(--faro-text-soft)] mb-4">Clics en el botón de teléfono por sede.</p>
          <div className="space-y-2">
            {centersOrder.map((cid) => (
              <CenterBar
                key={cid}
                label={centerLabels[cid] || cid}
                count={stats.events.phone_by_center_30[cid] || 0}
                max={maxPh}
                color="#e63946"
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-black text-lg">Visitas y tráfico (Google Analytics)</h3>
            <p className="text-sm text-[var(--faro-text-soft)] mt-1">
              Las <strong>visitas a la web</strong>, <strong>páginas más vistas</strong> y <strong>fuentes de tráfico</strong>
              (Google, Instagram, anuncios, directo, etc.) se miden con Google Analytics 4 en tu propia cuenta de Google.
              Los datos propios de la web (solicitudes, clics, inscripciones) los ves aquí arriba.
            </p>
            {config.ga_measurement_id ? (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider bg-green-100 text-green-800">
                  <span className="w-2 h-2 rounded-full bg-green-600" /> Conectado · {config.ga_measurement_id}
                </span>
                {gaLink && (
                  <a href={gaLink} target="_blank" rel="noreferrer" className="btn-pill btn-primary text-xs" data-testid="stats-ga-open">
                    <ExternalLink size={13} /> Abrir Google Analytics
                  </a>
                )}
              </div>
            ) : (
              <div className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider bg-yellow-100 text-yellow-800">
                <span className="w-2 h-2 rounded-full bg-yellow-500" /> Sin conectar
              </div>
            )}
          </div>
        </div>

        <details className="mt-4 rounded-xl bg-[var(--faro-bg)] p-4" open={!config.ga_measurement_id}>
          <summary className="cursor-pointer font-bold text-sm">Cómo conectar tu cuenta de Google Analytics (5 minutos)</summary>
          <ol className="mt-3 space-y-2 text-sm list-decimal ml-5 text-[var(--faro-text)]">
            <li>Entra en <a href="https://analytics.google.com/" target="_blank" rel="noreferrer" className="text-[var(--faro-blue)] font-bold underline">analytics.google.com</a> con tu cuenta de Google (o crea una nueva gratis).</li>
            <li>Pulsa <b>Admin</b> (rueda dentada abajo a la izquierda) → <b>Crear propiedad</b>. Nombre: <i>Autoescuelas Faro</i>. Zona horaria: <i>España</i>. Moneda: <i>EUR</i>.</li>
            <li>Sigue el asistente hasta llegar a la sección <b>Flujos de datos</b> → elige <b>Web</b>. URL del sitio: <code>https://www.autoescuelasfaro.com</code> (o la URL actual del preview).</li>
            <li>Google te dará un <b>Measurement ID</b> con formato <code>G-XXXXXXXXXX</code>. Cópialo.</li>
            <li>Pégalo abajo, guarda y ¡listo! A partir de esa recarga, cada visita se registrará automáticamente en tu Google Analytics.</li>
            <li>Para ver los informes: dentro de Analytics → <b>Informes</b> → <i>Adquisición → Adquisición de tráfico</i> (fuentes) o <i>Interacción → Páginas</i> (páginas más vistas). Copia la URL de tu informe favorito y pégala en el campo &ldquo;Enlace del panel&rdquo; para tenerlo a mano desde aquí.</li>
          </ol>
        </details>

        <form onSubmit={saveConfig} className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end" data-testid="stats-ga-form">
          <div className="md:col-span-1">
            <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Measurement ID</label>
            <input
              type="text"
              value={config.ga_measurement_id}
              onChange={(e) => setConfig({ ...config, ga_measurement_id: e.target.value.trim() })}
              placeholder="G-XXXXXXXXXX"
              data-testid="stats-ga-id"
              className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)] font-mono"
            />
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Enlace del panel (opcional)</label>
            <input
              type="url"
              value={config.ga_dashboard_url}
              onChange={(e) => setConfig({ ...config, ga_dashboard_url: e.target.value.trim() })}
              placeholder="https://analytics.google.com/analytics/web/#/..."
              data-testid="stats-ga-url"
              className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)]"
            />
          </div>
          <div className="md:col-span-1">
            <button type="submit" disabled={savingCfg} className="btn-pill btn-primary w-full justify-center disabled:opacity-60" data-testid="stats-ga-save">
              <Save size={15} /> {savingCfg ? "Guardando..." : "Guardar configuración"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// -------- Blog / News Admin Panel --------

const BLOG_CATEGORIES = ["Noticia", "Prensa", "TV", "Consejo", "Seguridad vial", "Curso"];

function guessMediaType(url = "") {
  const u = url.toLowerCase();
  if (u.startsWith("data:image") || u.match(/\.(png|jpe?g|gif|webp|avif|heic)(\?|$)/)) return "image";
  if (u.startsWith("data:video") || u.match(/\.(mp4|webm|ogg|mov|m4v)(\?|$)/)) return "video";
  if (u.match(/(youtube\.com|youtu\.be|vimeo\.com|facebook\.com\/plugins|dailymotion\.com|rtve\.es|tvg\.gal)/)) return "embed";
  return "image";
}

function normalizeEmbedUrl(url = "") {
  // Convert youtube/vimeo watch URLs into embed URLs
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
}

const EMPTY_BLOG = {
  id: null,
  title: "",
  slug: "",
  excerpt: "",
  category: "Noticia",
  content: "",
  image: "",
  media: [],
  read_minutes: 3,
  published: true,
};

function BlogPanel({ authHeader }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blog`, { headers: authHeader });
      setPosts(data);
    } catch {
      toast.error("No se pudieron cargar los artículos");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("¿Seguro que quieres eliminar este artículo? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/admin/blog/${id}`, { headers: authHeader });
      toast.success("Artículo eliminado");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  if (editing) {
    return (
      <BlogEditor
        authHeader={authHeader}
        post={editing}
        onSaved={() => { setEditing(null); load(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div data-testid="blog-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl">Blog y noticias <span className="text-[var(--faro-text-soft)] font-bold text-lg">({posts.length})</span></h2>
        <div className="flex gap-2 items-center flex-wrap">
          <button onClick={load} data-testid="blog-refresh" className="btn-pill btn-ghost text-sm"><RefreshCw size={15} /> Actualizar</button>
          <button onClick={() => setEditing({ ...EMPTY_BLOG })} data-testid="blog-new" className="btn-pill btn-primary text-sm"><Plus size={15} /> Nuevo artículo</button>
        </div>
      </div>

      <div className="rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--faro-bg)] text-left">
              <tr className="text-xs uppercase tracking-[0.1em] text-[var(--faro-blue)]">
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Título</th>
                <th className="px-4 py-3">Categoría</th>
                <th className="px-4 py-3">Multimedia</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(30,42,143,0.08)]">
              {loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--faro-text-soft)]">Cargando...</td></tr>
              )}
              {!loading && posts.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--faro-text-soft)]">Aún no hay artículos. Crea el primero con &ldquo;Nuevo artículo&rdquo;.</td></tr>
              )}
              {!loading && posts.map((p) => (
                <tr key={p.id} data-testid={`blog-row-${p.id}`} className={`hover:bg-[var(--faro-bg)] ${p.published ? "" : "opacity-60"}`}>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-black px-2.5 py-1 rounded-full ${p.published ? "bg-green-600 text-white" : "bg-gray-500 text-white"}`}>
                      {p.published ? <Eye size={11} /> : <EyeOff size={11} />}
                      {p.published ? "Publicado" : "Borrador"}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-[var(--faro-text-soft)]">{p.date || "—"}</td>
                  <td className="px-4 py-3 font-semibold max-w-md truncate" title={p.title}>{p.title}</td>
                  <td className="px-4 py-3"><span className="text-xs font-black uppercase tracking-wider text-[var(--faro-red)]">{p.category || "—"}</span></td>
                  <td className="px-4 py-3 text-[var(--faro-text-soft)] text-xs">
                    {(p.media?.length || 0)} elemento{(p.media?.length || 0) === 1 ? "" : "s"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => setEditing({ ...EMPTY_BLOG, ...p })} data-testid={`blog-edit-${p.id}`} className="btn-pill btn-ghost text-xs">Editar</button>
                      <button onClick={() => remove(p.id)} data-testid={`blog-delete-${p.id}`} className="btn-pill text-xs bg-red-50 text-[var(--faro-red)] hover:bg-red-100"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const UPLOAD_CHUNK_BYTES = 1024 * 1024;

const sliceToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

async function uploadFileToServer(file, authHeader, onProgress) {
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const total = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_BYTES));
  let res = null;
  for (let i = 0; i < total; i++) {
    const slice = file.slice(i * UPLOAD_CHUNK_BYTES, (i + 1) * UPLOAD_CHUNK_BYTES);
    const data = await sliceToBase64(slice);
    res = await axios.post(
      `${API}/admin/media/upload-chunk`,
      { upload_id: uploadId, chunk_index: i, total_chunks: total, filename: file.name, content_type: file.type || "application/octet-stream", data },
      { headers: authHeader }
    );
    if (onProgress) onProgress(Math.round(((i + 1) / total) * 100));
  }
  return res.data.url;
}

function BlogEditor({ authHeader, post, onSaved, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY_BLOG, ...post });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addUrl, setAddUrl] = useState("");
  const [preview, setPreview] = useState(false);

  const isNew = !form.id;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadCover = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("La imagen de portada supera 8 MB. Reduce el tamaño o usa una URL.");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadFileToServer(file, authHeader);
      upd("image", url);
      toast.success("Portada subida a tu dominio");
    } catch (e) {
      toast.error("No se pudo subir la portada");
    } finally {
      setUploading(false);
    }
  };

  const uploadMediaFile = async (file) => {
    if (!file) return;
    const isVideo = file.type.startsWith("video");
    if (isVideo && file.size > 20 * 1024 * 1024) {
      toast.error("Los vídeos por archivo tienen un límite de 20 MB. Sube el vídeo a YouTube/Vimeo y pega el enlace.");
      return;
    }
    if (!isVideo && file.size > 8 * 1024 * 1024) {
      toast.error("La imagen supera 8 MB.");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadFileToServer(file, authHeader);
      const item = {
        type: isVideo ? "video" : "image",
        url,
        caption: "",
      };
      setForm((f) => ({ ...f, media: [...(f.media || []), item] }));
      toast.success("Archivo subido a tu dominio");
    } catch (e) {
      toast.error("No se pudo subir el archivo");
    } finally {
      setUploading(false);
    }
  };

  const addMediaFromUrl = () => {
    if (!addUrl.trim()) return;
    const type = guessMediaType(addUrl);
    const url = type === "embed" ? normalizeEmbedUrl(addUrl) : addUrl;
    setForm((f) => ({ ...f, media: [...(f.media || []), { type, url, caption: "" }] }));
    setAddUrl("");
  };

  const updateMedia = (idx, patch) => {
    setForm((f) => ({ ...f, media: (f.media || []).map((m, i) => (i === idx ? { ...m, ...patch } : m)) }));
  };
  const removeMedia = (idx) => {
    setForm((f) => ({ ...f, media: (f.media || []).filter((_, i) => i !== idx) }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("El título es obligatorio");
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        slug: (form.slug || "").trim() || undefined,
        excerpt: form.excerpt || "",
        category: form.category || "Noticia",
        content: form.content || "",
        image: form.image || "",
        media: form.media || [],
        read_minutes: Number(form.read_minutes) || 3,
        published: !!form.published,
      };
      if (isNew) {
        await axios.post(`${API}/admin/blog`, payload, { headers: authHeader });
        toast.success("Artículo creado");
      } else {
        await axios.put(`${API}/admin/blog/${form.id}`, payload, { headers: authHeader });
        toast.success("Artículo actualizado");
      }
      if (payload.published) {
        toast.info("Recuerda desplegar la web: el artículo entrará en el sitemap con el próximo deploy", {
          duration: 12000,
          id: "deploy-reminder",
        });
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)]";
  const labelCls = "block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1";

  return (
    <div data-testid="blog-editor">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl">{isNew ? "Nuevo artículo" : "Editar artículo"}</h2>
        <div className="flex gap-2">
          <button onClick={onCancel} data-testid="blog-editor-cancel" className="btn-pill btn-ghost text-sm">Cancelar</button>
          <button type="button" onClick={() => setPreview(true)} data-testid="blog-editor-preview" className="btn-pill btn-secondary text-sm">
            <Eye size={15} /> Vista previa
          </button>
          <button onClick={save} disabled={saving} data-testid="blog-editor-save" className="btn-pill btn-primary text-sm disabled:opacity-60">
            <Save size={15} /> {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex flex-col" data-testid="blog-preview-overlay">
          <div className="bg-[var(--faro-blue-deep)] text-white px-5 py-3 flex items-center justify-between shrink-0">
            <span className="text-sm font-bold uppercase tracking-[0.15em]">Vista previa — así se verá publicado</span>
            <button type="button" onClick={() => setPreview(false)} data-testid="blog-preview-close" className="btn-pill bg-white/15 hover:bg-white/25 text-white text-sm">
              <XIcon size={15} /> Cerrar
            </button>
          </div>
          <div
            className="flex-1 overflow-y-auto bg-white"
            onClickCapture={(e) => {
              if (e.target.closest("a")) { e.preventDefault(); e.stopPropagation(); }
            }}
          >
            <ArticleBody post={{ ...form, read_minutes: Number(form.read_minutes) || 3 }} />
          </div>
        </div>
      )}

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4 bg-white rounded-2xl border border-[rgba(30,42,143,0.12)] p-5">
          <div>
            <label className={labelCls}>Título *</label>
            <input type="text" value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="blog-title" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Extracto</label>
            <textarea rows={2} value={form.excerpt} onChange={(e) => upd("excerpt", e.target.value)} data-testid="blog-excerpt" className={`${inputCls} resize-y`} />
          </div>
          <div>
            <label className={labelCls}>Contenido (párrafos separados por línea en blanco)</label>
            <textarea rows={12} value={form.content} onChange={(e) => upd("content", e.target.value)} data-testid="blog-content" className={`${inputCls} resize-y font-mono text-[13px]`} placeholder="Escribe aquí el cuerpo del artículo.&#10;&#10;Puedes usar párrafos separados por una línea en blanco." />
          </div>

          <div className="pt-4 border-t border-[rgba(30,42,143,0.08)]">
            <h3 className="font-display font-black text-lg mb-2 flex items-center gap-2"><ImageIcon size={18} /> Multimedia (prensa, TV, fotos)</h3>
            <p className="text-xs text-[var(--faro-text-soft)] mb-3">Sube imágenes de periódicos, capturas de TV o pega enlaces de YouTube / Vimeo / RTVE con la aparición.</p>

            <div className="flex flex-wrap gap-2 items-end mb-3">
              <label className="btn-pill btn-ghost text-sm cursor-pointer">
                <Upload size={14} /> Subir imagen o vídeo
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) => uploadMediaFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="blog-media-upload"
                />
              </label>
              <div className="flex gap-2 flex-1 min-w-[240px]">
                <input
                  type="url"
                  value={addUrl}
                  onChange={(e) => setAddUrl(e.target.value)}
                  data-testid="blog-media-url"
                  placeholder="URL de YouTube / Vimeo / imagen..."
                  className={inputCls}
                />
                <button type="button" onClick={addMediaFromUrl} data-testid="blog-media-add-url" className="btn-pill btn-ghost text-sm shrink-0">Añadir</button>
              </div>
            </div>

            {uploading && <div className="text-xs text-[var(--faro-blue)]">Procesando archivo...</div>}

            <div className="space-y-3">
              {(form.media || []).length === 0 && (
                <div className="text-sm text-[var(--faro-text-soft)] italic">Aún no has añadido multimedia.</div>
              )}
              {(form.media || []).map((m, i) => (
                <div key={m.url || i} data-testid={`blog-media-item-${i}`} className="flex items-start gap-3 rounded-xl border border-[rgba(30,42,143,0.12)] p-3">
                  <div className="w-24 h-16 rounded-lg overflow-hidden bg-[var(--faro-bg)] flex items-center justify-center shrink-0">
                    {m.type === "image" && <img src={m.url} alt="" className="w-full h-full object-cover" />}
                    {m.type === "video" && <Video size={20} className="text-[var(--faro-blue)]" />}
                    {m.type === "embed" && <div className="text-[var(--faro-red)] text-xs font-black">EMBED</div>}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <select
                        value={m.type}
                        onChange={(e) => updateMedia(i, { type: e.target.value })}
                        className="text-xs rounded-lg border border-[rgba(30,42,143,0.15)] px-2 py-1"
                      >
                        <option value="image">Imagen</option>
                        <option value="video">Vídeo (archivo)</option>
                        <option value="embed">YouTube / Vimeo</option>
                      </select>
                      <span className="text-[10px] text-[var(--faro-text-soft)] truncate flex-1" title={m.url}>{m.url.slice(0, 60)}{m.url.length > 60 ? "..." : ""}</span>
                    </div>
                    <input
                      type="text"
                      value={m.caption || ""}
                      onChange={(e) => updateMedia(i, { caption: e.target.value })}
                      placeholder="Pie de foto o descripción"
                      className="w-full text-sm rounded-lg border border-[rgba(30,42,143,0.15)] px-3 py-1.5"
                      data-testid={`blog-media-caption-${i}`}
                    />
                  </div>
                  <button type="button" onClick={() => removeMedia(i)} data-testid={`blog-media-remove-${i}`} className="w-8 h-8 rounded-lg bg-red-50 text-[var(--faro-red)] flex items-center justify-center hover:bg-red-100 shrink-0">
                    <XIcon size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-[rgba(30,42,143,0.12)] p-5 space-y-4">
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={form.published} onChange={(e) => upd("published", e.target.checked)} data-testid="blog-published" />
              Publicado (visible en la web)
            </label>
            <div>
              <label className={labelCls}>Categoría</label>
              <select value={form.category} onChange={(e) => upd("category", e.target.value)} data-testid="blog-category" className={inputCls}>
                {BLOG_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Minutos de lectura</label>
              <input type="number" min="1" value={form.read_minutes} onChange={(e) => upd("read_minutes", e.target.value)} data-testid="blog-read-minutes" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Slug (URL)</label>
              <input type="text" value={form.slug} onChange={(e) => upd("slug", e.target.value)} data-testid="blog-slug" placeholder="Se genera del título si lo dejas vacío" className={inputCls} />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[rgba(30,42,143,0.12)] p-5">
            <label className={labelCls}>Imagen de portada</label>
            {form.image && (
              <div className="mt-2 mb-3 rounded-xl overflow-hidden border border-[rgba(30,42,143,0.12)]">
                <img src={form.image} alt="Portada" className="w-full h-auto max-h-56 object-cover" data-testid="blog-cover-preview" />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <label className="btn-pill btn-ghost text-sm cursor-pointer justify-center">
                <Upload size={14} /> Subir imagen (máx. 2 MB)
                <input type="file" accept="image/*" onChange={(e) => uploadCover(e.target.files?.[0])} className="hidden" data-testid="blog-cover-upload" />
              </label>
              <input
                type="url"
                value={form.image}
                onChange={(e) => upd("image", e.target.value)}
                data-testid="blog-cover-url"
                placeholder="...o pega una URL de imagen"
                className={inputCls}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}


